<template>
  <q-page class="submit-page">
    <div class="submit-container">
      <!-- Loading state -->
      <div v-if="loading" class="submit-container">
        <q-skeleton type="text" width="200px" dark style="font-size: 1.5rem;" />
        <q-skeleton type="text" width="320px" dark style="margin-top: 4px;" />
        <q-skeleton type="rect" height="200px" dark style="border-radius: 16px; margin-top: 24px;" />
        <q-skeleton type="rect" height="300px" dark style="border-radius: 16px; margin-top: 16px;" />
      </div>

      <!-- Error state -->
      <div v-else-if="error" style="text-align: center; padding: 48px 16px;">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p style="color: var(--oa-text-muted); margin-top: 12px;">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchTemplate"
        />
      </div>

      <!-- Loaded -->
      <template v-else-if="template">
        <!-- Back link -->
        <router-link to="/evaluations" class="back-link">
          <q-icon name="arrow_back" size="14px" />
          Evaluaciones
        </router-link>

        <!-- Header -->
        <h1 class="page-title">Entregar evaluacion</h1>
        <p class="page-subtitle">{{ template.name }}</p>

        <!-- Template info card -->
        <div class="template-info-card">
          <p v-if="template.description" class="template-desc">
            {{ template.description }}
          </p>

          <!-- Badges -->
          <div class="template-badges">
            <q-badge
              v-if="template.course"
              outline
              :label="template.course.title"
              class="info-badge"
            />
            <q-badge
              v-if="template.unit"
              outline
              :label="`UD${template.unit.orderIndex + 1}`"
              class="info-badge"
            />
            <q-badge
              color="amber-8"
              text-color="black"
              :label="`${template.rubricConfig?.dimensions?.length ?? 0} dimensiones`"
              class="info-badge"
            />
          </div>

          <!-- Dimension preview -->
          <div class="template-dims-preview">
            <p class="dims-preview-title">Se evaluara segun estas dimensiones:</p>
            <div class="dims-list">
              <details
                v-for="dim in (template.rubricConfig?.dimensions ?? [])"
                :key="dim.key"
                class="dim-detail-preview"
              >
                <summary class="dim-summary-preview">
                  <div class="dim-bar-preview" />
                  <div class="dim-info-preview">
                    <span class="dim-label-preview">{{ dim.label }}</span>
                    <span class="dim-weight-preview">{{ dim.weight }}%</span>
                  </div>
                  <span class="dim-ind-count">
                    {{ dim.indicators.length }} indicadores
                  </span>
                  <q-icon name="expand_more" size="16px" class="dim-expand" />
                </summary>
                <div class="dim-indicators-preview">
                  <div
                    v-for="(ind, idx) in dim.indicators"
                    :key="ind.key"
                    class="ind-row-preview"
                  >
                    <span class="ind-num-preview">{{ idx + 1 }}</span>
                    <div class="ind-text-preview">
                      <p class="ind-label-preview">{{ ind.label }}</p>
                      <p v-if="ind.description" class="ind-desc-preview">{{ ind.description }}</p>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>

        <!-- Attempt counter banner -->
        <div v-if="myAttemptCount >= template.maxAttempts" class="attempt-banner attempt-exceeded">
          <q-icon name="warning" size="18px" />
          <span>Has alcanzado el maximo de {{ template.maxAttempts }} intentos para esta rubrica.</span>
        </div>
        <div v-else class="attempt-banner">
          <q-icon name="info" size="18px" />
          <span>Intento {{ myAttemptCount + 1 }} de {{ template.maxAttempts }}</span>
        </div>

        <!-- Submit form card -->
        <div class="submit-form-card">
          <form @submit.prevent="handleSubmit">
            <!-- Type selector -->
            <div class="form-field">
              <label class="field-label">Tipo de entrega</label>
              <div class="type-selector">
                <button
                  v-for="(label, type) in typeLabels"
                  :key="type"
                  type="button"
                  class="type-btn"
                  :class="{ 'type-btn-active': submissionType === type }"
                  @click="submissionType = type as SubmissionType"
                >
                  {{ label }}
                </button>
              </div>
            </div>

            <!-- Content field (text / practice_log) -->
            <div v-if="submissionType === 'text' || submissionType === 'practice_log'" class="form-field">
              <label class="field-label">
                {{ submissionType === 'text' ? 'Contenido' : 'Registro de practica' }}
              </label>
              <q-input
                v-model="content"
                type="textarea"
                :rows="8"
                dark
                outlined
                :placeholder="submissionType === 'text'
                  ? 'Escribe tu reflexion, analisis o respuesta...'
                  : 'Detalla la practica realizada, duracion, observaciones...'"
                :rules="[val => !!val || 'Este campo es obligatorio']"
              />
            </div>

            <!-- Video URL -->
            <div v-if="submissionType === 'video'" class="form-field">
              <label class="field-label">URL del video</label>
              <q-input
                v-model="videoUrl"
                dark
                outlined
                placeholder="https://www.youtube.com/watch?v=... o enlace de Google Drive"
                :rules="[val => !!val || 'Proporciona la URL del video']"
              />
              <p class="field-hint">YouTube, Google Drive, o cualquier enlace de video accesible</p>
            </div>

            <!-- Document URL -->
            <div v-if="submissionType === 'document'" class="form-field">
              <label class="field-label">URL del documento</label>
              <q-input
                v-model="fileUrl"
                dark
                outlined
                placeholder="https://docs.google.com/... o enlace de Google Drive"
                :rules="[val => !!val || 'Proporciona la URL del documento']"
              />
              <p class="field-hint">Google Docs, PDF en Drive, o enlace de descarga directa</p>
            </div>

            <!-- Link -->
            <div v-if="submissionType === 'link'" class="form-field">
              <label class="field-label">Enlace</label>
              <q-input
                v-model="content"
                dark
                outlined
                placeholder="https://..."
                :rules="[val => !!val || 'Proporciona el enlace']"
              />
            </div>

            <!-- Notes -->
            <div class="form-field">
              <label class="field-label">Notas adicionales (opcional)</label>
              <q-input
                v-model="notes"
                type="textarea"
                :rows="3"
                dark
                outlined
                placeholder="Contexto, aclaraciones, o comentarios para el evaluador..."
              />
            </div>

            <!-- Submit error -->
            <q-banner v-if="submitError" rounded class="submit-error-banner">
              <template #avatar>
                <q-icon name="error" color="red" />
              </template>
              {{ submitError }}
            </q-banner>

            <!-- Actions -->
            <div class="form-actions">
              <q-btn
                type="submit"
                no-caps
                unelevated
                :loading="submitting"
                :disable="submitting || myAttemptCount >= template.maxAttempts"
                label="Enviar entrega"
                class="submit-btn"
              />
              <q-btn
                no-caps
                flat
                label="Cancelar"
                to="/evaluations"
                style="color: var(--oa-text-muted);"
              />
            </div>
          </form>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';

// --- Types ---
type SubmissionType = 'video' | 'document' | 'link' | 'text' | 'practice_log';

interface RubricIndicator {
  key: string;
  label: string;
  description: string;
  maxScore: number;
}

interface RubricDimension {
  key: string;
  label: string;
  weight: number;
  indicators: RubricIndicator[];
}

interface EvalTemplate {
  id: string;
  name: string;
  description: string | null;
  rubricConfig: { dimensions: RubricDimension[] };
  passingScore: number;
  maxAttempts: number;
  isActive: boolean;
  course?: { title: string } | null;
  unit?: { title: string; orderIndex: number } | null;
}

interface Submission {
  id: string;
  templateId: string;
  attemptNumber: number;
}

// --- Constants ---
const typeLabels: Record<string, string> = {
  video: 'Video',
  document: 'Documento',
  link: 'Enlace',
  text: 'Texto',
  practice_log: 'Registro de Practica',
};

// --- Hooks ---
const route = useRoute();
const router = useRouter();
const $q = useQuasar();

// --- State ---
const loading = ref(true);
const error = ref<string | null>(null);
const template = ref<EvalTemplate | null>(null);
const myAttemptCount = ref(0);
const submitting = ref(false);
const submitError = ref<string | null>(null);

const submissionType = ref<SubmissionType>('text');
const content = ref('');
const videoUrl = ref('');
const fileUrl = ref('');
const notes = ref('');

// --- Data fetch ---
async function fetchTemplate() {
  loading.value = true;
  error.value = null;

  try {
    const templateId = route.params.templateId as string;
    const [templateRes, submissionsRes] = await Promise.all([
      api.get<EvalTemplate>(`/evaluations/templates/${templateId}`),
      api.get<Submission[]>('/evaluations/submissions/me'),
    ]);

    template.value = templateRes.data;

    // Count my attempts for this template
    const mySubmissions = (submissionsRes.data ?? []).filter(
      (s) => s.templateId === templateId,
    );
    myAttemptCount.value = mySubmissions.length;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error al cargar la rubrica';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

// --- Submit ---
async function handleSubmit() {
  if (!template.value) return;
  if (myAttemptCount.value >= template.value.maxAttempts) return;

  submitting.value = true;
  submitError.value = null;

  try {
    const body: Record<string, unknown> = {
      templateId: template.value.id,
      submissionType: submissionType.value,
    };

    if (submissionType.value === 'text' || submissionType.value === 'practice_log') {
      body.content = content.value || undefined;
    } else if (submissionType.value === 'link') {
      body.content = content.value || undefined;
    } else if (submissionType.value === 'video') {
      body.videoUrl = videoUrl.value || undefined;
    } else if (submissionType.value === 'document') {
      body.fileUrl = fileUrl.value || undefined;
    }

    if (notes.value) {
      body.notes = notes.value;
    }

    await api.post('/evaluations/submissions', body);

    $q.notify({
      type: 'positive',
      message: 'Entrega enviada',
      caption: 'Tu entrega fue registrada correctamente.',
      position: 'top',
      timeout: 3000,
    });

    router.push('/evaluations');
  } catch (err: unknown) {
    if (
      err &&
      typeof err === 'object' &&
      'response' in err
    ) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      submitError.value = resp?.data?.error ?? 'Error al enviar la entrega';
    } else {
      submitError.value = err instanceof Error ? err.message : 'Error desconocido';
    }
  } finally {
    submitting.value = false;
  }
}

onMounted(fetchTemplate);
</script>

<style lang="scss" scoped>
.submit-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.submit-container {
  max-width: 720px;
  width: 100%;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--oa-gold-full);
  text-decoration: none;
  margin-bottom: 16px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--oa-text-muted);
  font-size: 14px;
  margin: 4px 0 0;
}

// --- Template info card ---
.template-info-card {
  margin-top: 24px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.template-desc {
  font-size: 14px;
  color: var(--oa-text-secondary);
  margin: 0 0 12px;
  line-height: 1.5;
}

.template-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.info-badge {
  font-size: 11px;
}

// --- Dimension preview ---
.template-dims-preview {
  margin-top: 16px;
}

.dims-preview-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0 0 12px;
}

.dims-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dim-detail-preview {
  border-radius: 8px;
  overflow: hidden;
  background: var(--oa-bg-3);
  border: 1px solid var(--oa-border);

  &[open] .dim-expand {
    transform: rotate(180deg);
  }
}

.dim-summary-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
}

.dim-bar-preview {
  width: 6px;
  height: 32px;
  border-radius: 3px;
  background: var(--oa-gold-dim);
  flex-shrink: 0;
}

.dim-info-preview {
  flex: 1;
  min-width: 0;
}

.dim-label-preview {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
}

.dim-weight-preview {
  font-size: 12px;
  font-family: var(--oa-font-mono);
  color: var(--oa-gold-full);
  margin-left: 8px;
}

.dim-ind-count {
  font-size: 11px;
  color: var(--oa-text-muted);
}

.dim-expand {
  color: var(--oa-text-disabled);
  transition: transform 0.2s;
}

.dim-indicators-preview {
  padding: 4px 12px 12px;
  border-top: 1px solid var(--oa-border);
}

.ind-row-preview {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
}

.ind-num-preview {
  font-size: 10px;
  font-family: var(--oa-font-mono);
  width: 16px;
  text-align: right;
  color: var(--oa-text-disabled);
  flex-shrink: 0;
  margin-top: 2px;
}

.ind-text-preview {
  flex: 1;
}

.ind-label-preview {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-secondary);
  margin: 0;
}

.ind-desc-preview {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

// --- Attempt banner ---
.attempt-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: var(--oa-text-secondary);
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  margin-top: 16px;
}

.attempt-exceeded {
  background: color-mix(in srgb, var(--oa-danger) 8%, var(--oa-bg-2));
  border-color: color-mix(in srgb, var(--oa-danger) 20%, var(--oa-border));
  color: var(--oa-danger);
}

// --- Submit form card ---
.submit-form-card {
  margin-top: 16px;
  padding: 24px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.form-field {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin-bottom: 8px;
}

.field-hint {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

// --- Type selector ---
.type-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.type-btn {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid var(--oa-border);
  background: transparent;
  color: var(--oa-text-muted);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--oa-gold-dim);
    color: var(--oa-text-primary);
  }
}

.type-btn-active {
  border-color: var(--oa-gold-full);
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
  font-weight: 500;
}

// --- Submit error ---
.submit-error-banner {
  background: color-mix(in srgb, var(--oa-danger) 10%, var(--oa-bg-2));
  color: var(--oa-danger);
  margin-bottom: 16px;
}

// --- Actions ---
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.submit-btn {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 24px;
}
</style>
